import { createContext, useContext, useMemo, useRef, type ReactNode, type MutableRefObject } from "react"
import type { SerializedDockview } from "dockview"

// Shared wiring for hybrid persistence (APP-2705) between the seed/sync layer and the layout engine:
//   - instanceArrangement: the seeded arrangement the engine's load() returns in instance mode.
//   - arrangementGetterRef: live `() => api.toJSON()` reader the writer reads at flush time, so it
//     captures the current placement without re-rendering on every drag.
//   - requestSaveRef: the writer's save trigger, called on a Dockview arrangement change — which
//     doesn't dispatch to Redux, so the writer's store subscription would otherwise miss it.
export interface InstanceWorkspaceContextValue {
  instanceArrangement: SerializedDockview | null
  arrangementGetterRef: MutableRefObject<(() => Record<string, unknown> | null) | null>
  requestSaveRef: MutableRefObject<(() => void) | null>
}

const InstanceWorkspaceContext = createContext<InstanceWorkspaceContextValue | null>(null)

export function InstanceWorkspaceProvider({
  instanceArrangement,
  children,
}: {
  instanceArrangement: SerializedDockview | null
  children: ReactNode
}) {
  const arrangementGetterRef = useRef<(() => Record<string, unknown> | null) | null>(null)
  const requestSaveRef = useRef<(() => void) | null>(null)
  const value = useMemo(
    () => ({ instanceArrangement, arrangementGetterRef, requestSaveRef }),
    [instanceArrangement],
  )
  return <InstanceWorkspaceContext.Provider value={value}>{children}</InstanceWorkspaceContext.Provider>
}

// Returns null outside the provider (flag-off path never mounts it).
export function useInstanceWorkspaceContext(): InstanceWorkspaceContextValue | null {
  return useContext(InstanceWorkspaceContext)
}
